/**
 * What a RULE did, said as the rule doing it (Jeremy, 22 Oct 2026, punch list
 * #7). A row with no person on it rendered as "Someone unblocked Peer review",
 * which reads as a person whose name we lost. Nobody did these; the sentence
 * names what did.
 *
 * The ONE place a rule's label becomes words, for the task history and the
 * Inbox alike. Keyed by `RuleActor`, so a rule added to the list without
 * wording here does not compile.
 */
import { isRuleActor, mdy } from "@fieldpath/shared";
import type { ActivityRow, RuleActor } from "@fieldpath/shared";

/** Each rule as the subject of a sentence: "The resource scheduler assigned you to …". */
const RULE_NAME: Record<RuleActor, string> = {
  "system:stage-condition": "An automatic release",
  "system:snooze-date": "A check-back date",
  "system:hibernation-sweep": "Automatic hibernation",
  "system:resource-scheduler": "The resource scheduler",
  "system:end-stage": "Finish on arrival",
};

/** A rule's name. One missing from the list still reads as a rule, never a person. */
export function ruleName(label: string): string {
  return isRuleActor(label) ? RULE_NAME[label] : "An automatic rule";
}

/**
 * One history row a rule wrote: the bold lead, and the rest of the sentence --
 * or null for the rest when the row's ordinary description reads right after
 * the rule's name ("The resource scheduler changed the due date …").
 */
export function ruleActivity(
  row: ActivityRow,
  label: string,
): { lead: string; rest: string | null } {
  const stage = row.stageName ?? "this stage";
  const release = row.release;

  if (row.action === "unblocked") {
    if (release?.kind === "stage") {
      return {
        lead: "Released automatically:",
        rest: `${stage}, when ${release.taskTitle} reached ${release.stageName}`,
      };
    }
    if (release?.kind === "date") {
      return {
        lead: "Released automatically:",
        rest: `${stage}, when its check-back date, ${mdy(release.date)}, arrived`,
      };
    }
    // The awaited stage has since been deleted; the rule is still known.
    if (label === "system:stage-condition") {
      return { lead: "Released automatically:", rest: `${stage}, when the stage it waited on was reached` };
    }
    if (label === "system:snooze-date") {
      return { lead: "Released automatically:", rest: `${stage}, when its check-back date arrived` };
    }
  }
  if (row.action === "hibernated" && label === "system:hibernation-sweep") {
    return { lead: "Hibernated automatically:", rest: `${stage}, after being blocked past the limit` };
  }
  if (row.action === "completed" && label === "system:end-stage") {
    return { lead: "Finished on arrival:", rest: `${stage} completed itself when the work reached it` };
  }
  return { lead: ruleName(label), rest: null };
}
